import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getAllUsers, blockUser, unblockUser, deleteUser, updateUserRole, exportData, createUser, updateUser, seedUsers } from '@/services/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Search,
  Shield,
  ShieldOff,
  Trash2,
  UserCog,
  Users,
  Download,
  FileSpreadsheet,
  FileText,
  FolderKanban,
  ClipboardCheck,
  BarChart3,
  Zap,
  Plus,
  Edit,
  Star,
  Activity
} from 'lucide-react';

// User Management Page
export const UserManagementPage = () => {
  const { user: currentUser, isMasterAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    company: '',
    industry: 'logistics'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await getAllUsers();
      setUsers(data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedUsers = async () => {
    try {
      await seedUsers();
      toast.success('Default users created/updated');
      loadUsers();
    } catch (error) {
      toast.error('Failed to seed users');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(newUser.email.trim())) {
      toast.error('Please enter a valid email address (required for FlowCrew login)');
      return;
    }
    
    try {
      await createUser({ ...newUser, email: newUser.email.trim().toLowerCase() });
      toast.success('User created successfully');
      setShowCreateDialog(false);
      setNewUser({ name: '', email: '', password: '', role: 'user', company: '', industry: 'logistics' });
      loadUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleEditUser = async () => {
    if (!editingUser) return;
    if (!editingUser.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(editingUser.email.trim())) {
      toast.error('A valid email address is required (used for FlowCrew login)');
      return;
    }
    
    try {
      await updateUser(editingUser.user_id, { ...editingUser, email: editingUser.email.trim().toLowerCase() });
      toast.success('User updated successfully');
      setShowEditDialog(false);
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleBlock = async (userId) => {
    try {
      await blockUser(userId);
      setUsers(users.map(u => u.user_id === userId ? { ...u, is_blocked: true } : u));
      toast.success('User blocked');
    } catch (error) {
      toast.error(error.message || 'Failed to block user');
    }
  };

  const handleUnblock = async (userId) => {
    try {
      await unblockUser(userId);
      setUsers(users.map(u => u.user_id === userId ? { ...u, is_blocked: false } : u));
      toast.success('User unblocked');
    } catch (error) {
      toast.error(error.message || 'Failed to unblock user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    
    try {
      await deleteUser(userId);
      setUsers(users.filter(u => u.user_id !== userId));
      toast.success('User deleted');
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      setUsers(users.map(u => u.user_id === userId ? { ...u, role: newRole } : u));
      toast.success('User role updated');
    } catch (error) {
      toast.error(error.message || 'Failed to update role');
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(search.toLowerCase()) ||
                         u.email?.toLowerCase().includes(search.toLowerCase());
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="user-management-page">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">User Management</h1>
            <p className="text-muted-foreground font-body mt-1">
              Manage users, roles, and access permissions
              {!isMasterAdmin && <span className="text-amber-600 ml-2">(Read-only - Master Admin required for changes)</span>}
            </p>
          </div>
          
          {isMasterAdmin && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleSeedUsers} data-testid="seed-users-btn">
                <Star className="h-4 w-4 mr-2" />
                Initialize Accounts
              </Button>
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="create-user-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add User
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New User</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Name *</label>
                      <Input 
                        value={newUser.name}
                        onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                        placeholder="Full name"
                        data-testid="new-user-name"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Email *</label>
                      <Input 
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        placeholder="name@company.com"
                        data-testid="new-user-email"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Required — used as the login for the FlowCrew mobile app.</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Password *</label>
                      <PasswordInput 
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        placeholder="Password"
                        testId="new-user-password"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Role</label>
                        <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">User</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Industry</label>
                        <Select value={newUser.industry} onValueChange={(v) => setNewUser({...newUser, industry: v})}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="logistics">Logistics</SelectItem>
                            <SelectItem value="manufacturing">Manufacturing</SelectItem>
                            <SelectItem value="healthcare">Healthcare</SelectItem>
                            <SelectItem value="retail">Retail</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Company</label>
                      <Input 
                        value={newUser.company}
                        onChange={(e) => setNewUser({...newUser, company: e.target.value})}
                        placeholder="Company name"
                      />
                    </div>
                    <Button onClick={handleCreateUser} className="w-full" data-testid="submit-create-user">
                      Create User
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm bg-blue-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm bg-purple-50 flex items-center justify-center">
                <Star className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.is_master_admin).length}</p>
                <p className="text-sm text-muted-foreground">Master Admin</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm bg-emerald-50 flex items-center justify-center">
                <Shield className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'admin' && !u.is_master_admin).length}</p>
                <p className="text-sm text-muted-foreground">Admins</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-sm bg-rose-50 flex items-center justify-center">
                <ShieldOff className="h-5 w-5 text-rose-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.is_blocked).length}</p>
                <p className="text-sm text-muted-foreground">Blocked</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-users"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-[150px]" data-testid="filter-role">
              <SelectValue placeholder="All Roles" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              <SelectItem value="master_admin">Master Admin</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="user">User</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Users Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => (
                    <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={u.picture} />
                            <AvatarFallback className="text-xs">{u.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium flex items-center gap-2">
                              {u.name}
                              {u.is_master_admin && <Star className="h-4 w-4 text-amber-500" />}
                            </p>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {u.is_master_admin ? (
                          <Badge className="bg-amber-100 text-amber-800">Master Admin</Badge>
                        ) : isMasterAdmin && u.user_id !== currentUser?.user_id ? (
                          <Select
                            value={u.role}
                            onValueChange={(v) => handleRoleChange(u.user_id, v)}
                          >
                            <SelectTrigger className="w-[100px]" data-testid={`role-select-${u.user_id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="user">User</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <Badge variant="outline" className="capitalize">{u.role}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {u.industry || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell data-testid={`user-phone-${u.user_id}`}>
                        {u.phone
                          ? <span className="text-xs font-mono">{u.phone}</span>
                          : <span className="text-xs text-muted-foreground italic">— not set —</span>}
                      </TableCell>
                      <TableCell>
                        {u.is_blocked ? (
                          <Badge variant="destructive">Blocked</Badge>
                        ) : (
                          <Badge variant="secondary">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {isMasterAdmin && u.user_id !== currentUser?.user_id && !u.is_master_admin && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingUser({...u, password: ''});
                                  setShowEditDialog(true);
                                }}
                                data-testid={`edit-${u.user_id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              {u.is_blocked ? (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleUnblock(u.user_id)}
                                  data-testid={`unblock-${u.user_id}`}
                                >
                                  <Shield className="h-4 w-4" />
                                </Button>
                              ) : (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleBlock(u.user_id)}
                                  data-testid={`block-${u.user_id}`}
                                >
                                  <ShieldOff className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDelete(u.user_id)}
                                data-testid={`delete-${u.user_id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
        
        {/* Edit User Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            {editingUser && (
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Name</label>
                  <Input 
                    value={editingUser.name}
                    onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Email *</label>
                  <Input 
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                    placeholder="name@company.com"
                    data-testid="edit-user-email"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Required — used as the login for the FlowCrew mobile app.</p>
                </div>
                <div>
                  <label className="text-sm font-medium">New Password (leave blank to keep current)</label>
                  <PasswordInput 
                    value={editingUser.password || ''}
                    onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
                    placeholder="Enter new password"
                    testId="edit-user-password"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Role</label>
                    <Select value={editingUser.role} onValueChange={(v) => setEditingUser({...editingUser, role: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Industry</label>
                    <Select value={editingUser.industry || 'logistics'} onValueChange={(v) => setEditingUser({...editingUser, industry: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="logistics">Logistics</SelectItem>
                        <SelectItem value="manufacturing">Manufacturing</SelectItem>
                        <SelectItem value="healthcare">Healthcare</SelectItem>
                        <SelectItem value="retail">Retail</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Company</label>
                  <Input 
                    value={editingUser.company || ''}
                    onChange={(e) => setEditingUser({...editingUser, company: e.target.value})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Phone <span className="text-xs text-muted-foreground font-normal">(required for messaging)</span></label>
                  <Input
                    value={editingUser.phone || ''}
                    onChange={(e) => setEditingUser({...editingUser, phone: e.target.value})}
                    placeholder="+31612345678"
                    data-testid="edit-user-phone"
                  />
                </div>
                <Button onClick={handleEditUser} className="w-full" data-testid="submit-edit-user">
                  Save Changes
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// Export Data Page
export const ExportDataPage = () => {
  const { isMasterAdmin } = useAuth();
  const [exporting, setExporting] = useState(null);

  const handleExport = async (type, format) => {
    setExporting(`${type}-${format}`);
    try {
      const data = await exportData(type, format);
      
      if (format === 'json') {
        // Create downloadable JSON file
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `kaizen-kanri-${type}-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // data is already a blob for excel/docx
        const url = window.URL.createObjectURL(data);
        const a = document.createElement('a');
        a.href = url;
        const ext = format === 'excel' ? 'xlsx' : 'docx';
        a.download = `kaizen-kanri-${type}-${new Date().toISOString().split('T')[0]}.${ext}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
      
      toast.success(`${type} exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    } finally {
      setExporting(null);
    }
  };

  const exportOptions = [
    {
      id: 'projects',
      title: 'DMAIC Projects',
      description: 'Export all projects with phases and metrics',
      icon: FolderKanban,
      color: 'bg-blue-50 text-blue-600',
      formats: ['json', 'excel', 'docx']
    },
    {
      id: 'audits',
      title: '6S Audits',
      description: 'Export all audits with scores and notes',
      icon: ClipboardCheck,
      color: 'bg-emerald-50 text-emerald-600',
      formats: ['json', 'excel', 'docx']
    },
    {
      id: 'kpis',
      title: 'KPI Metrics',
      description: 'Export all KPIs with values and targets',
      icon: BarChart3,
      color: 'bg-violet-50 text-violet-600',
      formats: ['json', 'excel']
    },
    {
      id: 'kaizen-events',
      title: 'Kaizen Events',
      description: 'Export all improvement events and outcomes',
      icon: Zap,
      color: 'bg-amber-50 text-amber-600',
      formats: ['json', 'excel']
    },
    {
      id: 'checklists',
      title: 'Checklists',
      description: 'Export all assigned checklists with progress',
      icon: ClipboardCheck,
      color: 'bg-cyan-50 text-cyan-600',
      formats: ['json', 'excel', 'docx']
    }
  ];

  // Activity logs - only for Master Admin
  const activityLogOption = {
    id: 'activity-logs',
    title: 'Activity Logs',
    description: 'Export all user activity logs with timestamps',
    icon: Activity,
    color: 'bg-rose-50 text-rose-600',
    formats: ['json', 'excel']
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="export-data-page">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Export Data</h1>
          <p className="text-muted-foreground font-body mt-1">
            Download your data in JSON, Excel, or Word format
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {exportOptions.map((option) => {
            const Icon = option.icon;
            return (
              <Card key={option.id} className="card-hover">
                <CardHeader>
                  <div className={`w-12 h-12 rounded-sm flex items-center justify-center mb-4 ${option.color}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <CardTitle>{option.title}</CardTitle>
                  <CardDescription>{option.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleExport(option.id, 'json')}
                      disabled={!!exporting}
                      data-testid={`export-${option.id}-json`}
                      className="flex-col h-auto py-3"
                    >
                      <FileText className="h-5 w-5 mb-1" />
                      <span className="text-xs">JSON</span>
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleExport(option.id, 'excel')}
                      disabled={!!exporting}
                      data-testid={`export-${option.id}-excel`}
                      className="flex-col h-auto py-3"
                    >
                      <FileSpreadsheet className="h-5 w-5 mb-1 text-emerald-600" />
                      <span className="text-xs">Excel</span>
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleExport(option.id, 'docx')}
                      disabled={!!exporting || !option.formats.includes('docx')}
                      data-testid={`export-${option.id}-docx`}
                      className="flex-col h-auto py-3"
                    >
                      <FileText className="h-5 w-5 mb-1 text-blue-600" />
                      <span className="text-xs">Word</span>
                    </Button>
                  </div>
                  {exporting?.startsWith(option.id) && (
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      Exporting...
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Activity Logs - Master Admin Only */}
          {isMasterAdmin && (
            <Card className="card-hover border-rose-200">
              <CardHeader>
                <div className={`w-12 h-12 rounded-sm flex items-center justify-center mb-4 ${activityLogOption.color}`}>
                  <Activity className="h-6 w-6" />
                </div>
                <CardTitle>{activityLogOption.title}</CardTitle>
                <CardDescription>{activityLogOption.description}</CardDescription>
                <Badge variant="outline" className="w-fit mt-2">Master Admin Only</Badge>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleExport('activity-logs', 'json')}
                    disabled={!!exporting}
                    data-testid="export-activity-logs-json"
                    className="flex-col h-auto py-3"
                  >
                    <FileText className="h-5 w-5 mb-1" />
                    <span className="text-xs">JSON</span>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleExport('activity-logs', 'excel')}
                    disabled={!!exporting}
                    data-testid="export-activity-logs-excel"
                    className="flex-col h-auto py-3"
                  >
                    <FileSpreadsheet className="h-5 w-5 mb-1 text-emerald-600" />
                    <span className="text-xs">Excel</span>
                  </Button>
                  <Button
                    variant="outline"
                    disabled
                    className="flex-col h-auto py-3 opacity-50"
                  >
                    <FileText className="h-5 w-5 mb-1 text-blue-600" />
                    <span className="text-xs">Word</span>
                  </Button>
                </div>
                {exporting === 'activity-logs-json' || exporting === 'activity-logs-excel' ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    Exporting...
                  </div>
                ) : null}
              </CardContent>
            </Card>
          )}
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Export Tips</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <div className="flex items-start gap-3">
              <FileSpreadsheet className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <p><strong>Excel:</strong> Best for data analysis, pivot tables, and custom reporting in spreadsheet applications.</p>
            </div>
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p><strong>Word:</strong> Best for formatted reports, presentations, and documentation.</p>
            </div>
            <div className="flex items-start gap-3">
              <Download className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <p><strong>JSON:</strong> Best for data backup, API integrations, and importing into other systems.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default UserManagementPage;
